import { creditCardSchema } from 'auth/schemas'
import { pendingSelector } from 'api/selectors'
import { createDenormalizedSelector, entitiesSelector } from 'entities/selectors'
import { rateSchema } from 'grs/schemas'

import { isNifRequiredAndMissingSelector, userSelector, addressIsSetSelector } from 'auth/selectors'
import featureToggles from 'shared/featureToggles'
import { GOOGLE_PAY_METHOD } from 'shared/transactions'
import { selectedEventLocationIdSelector } from 'search/selectors'
import { createSelector } from 'reselect'

export const stateSelector = state => state.booking

export const selectedRateIdSelector = createSelector(stateSelector, state => state.selectedRate)
export const bookingItemSelector = createSelector(stateSelector, state => state.bookingItem)
export const selectedVehicleSelector = createSelector(stateSelector, state => state.selectedVehicles)
export const selectedCreditCardIdSelector = createSelector(stateSelector, state => state.card)
export const selectedCreditCardSelector = createDenormalizedSelector(selectedCreditCardIdSelector, creditCardSchema)

export const isGooglePaySelectedSelector = createSelector(
    selectedCreditCardSelector,
    creditCard => creditCard?.id === GOOGLE_PAY_METHOD
)

export const disabledCardsSelector = createSelector(stateSelector, state => state.disabledCards)

export const bookingRatesSelector = createSelector(stateSelector, state =>
    state.rates instanceof Array ? state.rates : []
)
export const locationDetailsSelector = createSelector(stateSelector, state => state.bookingLocationDetails)

export const locationTimezoneSelector = createSelector(locationDetailsSelector, location => location.timeZone)

export const locationDataSelector = createSelector(selectedRateIdSelector, state => state?.locationData)

export const ratesPendingSelector = createSelector(pendingSelector, pending => pending.bookingRates)
export const ratesErrorSelector = createSelector(
    ratesPendingSelector,
    pending => pending instanceof Error && pending.message
)
export const ratesAreLoadingSelector = createSelector(
    ratesPendingSelector,
    ratesPending => ratesPending && !(ratesPending instanceof Error)
)

export const locationDetailsPendingSelector = createSelector(pendingSelector, pending => pending.locationDetails)

export const unfilteredRatesSelector = createDenormalizedSelector(bookingRatesSelector, [rateSchema])
export const vehiclesSelector = createSelector(userSelector, user => (user ? user.vehicles : []))
export const doShowSubscriptionsSelector = (_, { showSubscriptions }) => showSubscriptions

export const selectedVehicleDetailsSelector = createSelector(
    selectedVehicleSelector,
    entitiesSelector,
    (selectedVehicles, entities) => {
        if (selectedVehicles.length === 0) return null
        if (!entities?.vehicles) return null

        const selectedVehicleId = selectedVehicles[0]?.id
        return entities?.vehicles[selectedVehicleId] || null
    }
)

export const ratesSelector = createSelector(
    unfilteredRatesSelector,
    doShowSubscriptionsSelector,
    (rates, showSubscriptions) =>
        rates.filter(rate => (rate.isMonthlyPass && showSubscriptions) || (!rate.isMonthlyPass && !showSubscriptions))
)
export const rateSelector = createDenormalizedSelector(selectedRateIdSelector, rateSchema)

export const promoCodeSelector = createSelector(stateSelector, state => state.promoCode)
export const promoCodeRedeemStatusByIndexSelector = createSelector(
    pendingSelector,
    (_, index) => index,
    (pending, index) => {
        const pendingValue = pending[`redeemPromoCode${index}`]
        return {
            isPromoCodeLoading: pendingValue === true,
            promoCodePendingError: pendingValue instanceof Error ? pendingValue.message : '',
        }
    }
)
export const lastBookingDetailsSelector = createSelector(stateSelector, state => state.lastBookingDetails)

export const selectedBookingItemSelector = createSelector(
    lastBookingDetailsSelector,
    selectedVehicleSelector,
    promoCodeSelector,
    (lastBookingDetails, vehicles, promoCode) => {
        if (lastBookingDetails) {
            return {
                id: lastBookingDetails.id,
                rate: { ...lastBookingDetails.rate, waitingListKey: lastBookingDetails.waitingListKey },
                promoCode,
                vehicles,
                autoRenew: lastBookingDetails.autoRenew,
            }
        } else {
            return null
        }
    }
)

export const promoCodeBarcodeSelector = createSelector(selectedBookingItemSelector, selectedBookingItem => {
    if (selectedBookingItem && selectedBookingItem.promoCode) {
        return selectedBookingItem.promoCode.barcode
    }
    return null
})

export const rawPromoCodeSelector = createSelector(stateSelector, state => state.rawPromoCode)
export const currentCustomerFlowSelector = createSelector(stateSelector, state => state.customerFlow)

export const lastBookingLinkSelector = createSelector(lastBookingDetailsSelector, details => {
    if (details) {
        const { grsId, rateId, subscriptions, waitingListKey } = details

        return {
            pathname: `/booking/${grsId}${subscriptions ? '/subscriptions' : ''}`,
            search: { rateId, waitingListKey },
        }
    }

    return null
})

export const seasonTicketsSelector = createSelector(stateSelector, ({ seasonTickets }) => seasonTickets)
export const selectedSeasonTicketSelector = createSelector(
    stateSelector,
    ({ selectedSeasonTicket }) => selectedSeasonTicket
)
export const selectedTicketRatePendingSelector = createSelector(pendingSelector, pending => pending.getSeasonTicketRate)
export const seasonTicketsPendingSelector = createSelector(pendingSelector, pending => pending.getSeasonTickets)
export const waitingListKeySelector = createSelector(stateSelector, state => state.waitingListKey)
export const waitingListPendingSelector = createSelector(
    pendingSelector,
    pending => pending.getWaitingListRate || pending.validateWaitingListKey
)
export const waitingListNextAvailableDateSelector = createSelector(
    stateSelector,
    state => state.waitingListNextAvailableDate
)
export const parkingModeSelector = createSelector(stateSelector, state => state.parkingMode)

export const eventBookingUrlSelector = createSelector(selectedEventLocationIdSelector, selectedEventLocationId =>
    selectedEventLocationId ? `/booking/${selectedEventLocationId}/events` : null
)

export const upSellOptionsSelector = createSelector(stateSelector, state => state.upSellOptions?.rateUpSellOptions)
export const selectedUpSellOptionSelector = createSelector(stateSelector, state => state.selectedUpSellOption)

export const threeDsSelector = createSelector(stateSelector, state => state.threeDs)

export const getUpSellOptionPendingSelector = createSelector(pendingSelector, pending => pending.getUpSellOptions)
export const upgradeUpSellOptionPendingSelector = createSelector(
    pendingSelector,
    pending => pending.upgradeUpSellOption
)
export const upgradeUpSellErrorSelector = createSelector(
    upgradeUpSellOptionPendingSelector,
    pending => pending instanceof Error && pending.message
)

export const additionalParkingItemsSelector = createSelector(stateSelector, state => state.additionalParkingItems)

export const addtionalItemsPendingSelector = createSelector(
    pendingSelector,
    pending => pending.GET_ADDITIONAL_PARKING_ITEMS
)

export const threeDsDataSelector = createSelector(threeDsSelector, threeDs => threeDs)

export const guestUserCardVerifiedSelector = createSelector(
    stateSelector,
    state => state.guestUserChekoutData?.guestUserCardIsVerified
)
export const guestUserPersonalDataSelector = createSelector(
    stateSelector,
    state => state.guestUserChekoutData?.guestUserPersonalData
)

export const makePurchasePendingSelector = createSelector(pendingSelector, pending => pending.makePurchase)

export const allowPurchaseSingleTicketSelector = createSelector(
    addressIsSetSelector,
    makePurchasePendingSelector,
    isNifRequiredAndMissingSelector,
    (addressIsSet, makePurchasePending, isNifRequiredAndMissing) =>
        makePurchasePending !== true && addressIsSet && !isNifRequiredAndMissing
)

export const purchaseCompletedSelector = createSelector(stateSelector, state => state.purchaseCompleted)

export const eventRatePendingSelector = createSelector(pendingSelector, pending => pending.eventRate || false)

export const activeCardIdSelector = createSelector(
    selectedCreditCardSelector,
    guestUserCardVerifiedSelector,
    (selectedCreditCard, guestUserCard) => selectedCreditCard?.id ?? guestUserCard?.id
)

export const activeCardBinSelector = createSelector(
    selectedCreditCardSelector,
    guestUserCardVerifiedSelector,
    (selectedCreditCard, guestUserCard) => selectedCreditCard?.bin ?? guestUserCard?.bin
)

export const isPromoAppliedByCardSelector = createSelector(
    promoCodeSelector,
    activeCardIdSelector,
    (promoCode, activeCardId) => featureToggles.hasBMOPromo && activeCardId != null && promoCode?.statusCode === 200
)
